/**
 * camera feature – all constants (camera, layout, preview, image editor).
 */

// ----- Camera / video / shutter -----
export const SHUTTER_SOUND_SRC =
  "data:audio/mp3;base64,SUQzAwAAAAAAF1RTU0MAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA//tQxAADB0NAgQAAQAAAwAADAARkwAA" +
  "AAAAFBQUEBQUFgAVAAABcAAAAEAAAAYAABAAFCRAABAEBAAAQAIAAQAAEBCAAQABAAEAwAEQAIAAQIBAAQAAQACAAAAAAQAAQABIiAg" +
  "AgAAAA==";

export const CAMERA_HEIGHT = 1000;
export const DEFAULT_VIDEO_CONSTRAINTS = {
  width: 1280,
  height: 720,
  facingMode: "user",
};
export const DEFAULT_CROP = {
  unit: "%",
  width: 95,
  height: 95,
  y: 2.5,
  x: 2.5,
  aspect: undefined,
};
export const MAX_CAPTURE_SIZE_BYTES = 1 * 1024 * 1024;
export const ALLOW_VIDEO_CAPTURE = false;

// ----- Camera preview -----
export const SHUTTER_VISIBLE_MS = 300;
export const FOCUS_INDICATOR_DURATION_MS = 800;
export const PINCH_ZOOM_SENSITIVITY = 0.5;
export const PINCH_ZOOM_THRESHOLD = 0.05;
export const DEFAULT_ZOOM = Object.freeze({ min: 1, max: 4, step: 0.25 });

// ----- Layout (CameraCapture modal) -----
export const COMPRESS_IMAGE_FILE = true;
export const TOUCH_TARGET_MIN = 44;
export const HEADER_HEIGHT = 56;
export const FOOTER_HEIGHT = "14dvh";
export const FOOTER_ICON_SIZE = 56;

export const OVERLAY_STYLE = Object.freeze({
  width: "100dvw",
  height: "100dvh",
  maxHeight: "100dvh",
  position: "fixed",
  top: 0,
  left: 0,
  zIndex: 1000,
});

const HEADER_BASE = {
  width: "100%",
  paddingLeft: "max(12px, env(safe-area-inset-left))",
  paddingRight: "max(12px, env(safe-area-inset-right))",
  paddingTop: "max(12px, env(safe-area-inset-top))",
  paddingBottom: 12,
  minHeight: HEADER_HEIGHT,
  display: "flex",
  flexWrap: "nowrap",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 8,
  flexShrink: 0,
};

export const HEADER_STYLE_FRAMED = Object.freeze({
  ...HEADER_BASE,
  background: "#000",
  zIndex: 10,
});

export const HEADER_STYLE_FULLSCREEN = Object.freeze({
  ...HEADER_BASE,
  position: "absolute",
  top: 0,
  left: 0,
  right: 0,
  zIndex: 10,
  background: "linear-gradient(to bottom, rgba(0,0,0,0.7) 0%, transparent 100%)",
});

export const FOOTER_STYLE_FRAMED = Object.freeze({
  background: "#000",
  minHeight: 80,
  height: FOOTER_HEIGHT,
  flexShrink: 0,
  paddingBottom: "max(12px, env(safe-area-inset-bottom))",
});

export const FOOTER_STYLE_FULLSCREEN = Object.freeze({
  position: "absolute",
  bottom: 0,
  left: 0,
  right: 0,
  minHeight: 80,
  height: FOOTER_HEIGHT,
  paddingBottom: "max(12px, env(safe-area-inset-bottom))",
  background: "linear-gradient(to top, rgba(0,0,0,0.75) 0%, transparent 100%)",
});

export const CONTROLS_STYLE_BASE = Object.freeze({
  display: "flex",
  justifyContent: "space-around",
  alignItems: "center",
  color: "white",
});

export const FLASH_MODES = Object.freeze(["off", "on", "auto"]);
export const ZOOM_PRESETS = Object.freeze([0.5, 1, 2, 3]);

export const STACK_STRIP_CSS = `
  @keyframes cameraStackStripOpen {
    from { opacity: 0; transform: translateY(100%); }
    to { opacity: 1; transform: translateY(0); }
  }
  .camera-stack-strip-animate {
    animation: cameraStackStripOpen 0.28s ease-out forwards;
  }
  @keyframes cameraStackStripClose {
    from { opacity: 1; transform: translateY(0); }
    to { opacity: 0; transform: translateY(100%); }
  }
  .camera-stack-strip-close {
    animation: cameraStackStripClose 0.24s ease-in forwards;
  }
`;

// ----- Image editor -----
/** When true, the image editor toolbar shows the Rotate option. Set to false to hide rotation in the editor. */
export const ALLOW_IMAGE_EDITOR_ROTATE = false;
export const JPEG_QUALITY = 0.95;
export const TOOLBAR_HEIGHT = 48;
export const CROP_IMAGE_STYLE = Object.freeze({
  maxHeight: "min(70vh, 70dvh)",
  maxWidth: "min(90vw, 90dvw)",
  width: "auto",
  height: "auto",
  margin: "auto",
});
export const PDF_VIEWER_STYLE = Object.freeze({ width: "96vw", height: "85vh" });
export const DOC_VIEWER_BUTTONS = Object.freeze(["zoomIn", "zoomOut", "rotate", "reset"]);
export const ASPECT_OPTIONS = Object.freeze([
  { label: "Free", value: undefined },
  // { label: "1:1", value: 1 },
  // { label: "4:3", value: 4 / 3 },
  // { label: "16:9", value: 16 / 9 },
]);
